"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { getProductsClient } from "@/lib/firebase/services/productsClient";
import {
  PROFESSIONAL_SERVICES_ANNUAL_PRICE_LEGACY,
  PROFESSIONAL_SERVICES_ANNUAL_PRODUCT_CODE,
  PROFESSIONAL_SERVICES_MONTHLY_PRICE_LEGACY,
  PROFESSIONAL_SERVICES_MONTHLY_PRODUCT_CODE,
} from "@/lib/professionalServices/professionalServicesPlan";
import type { Product } from "@/types/product";

type BillingCycle = "monthly" | "annual";

const formatZAR = (amount: number) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(amount);

const getEffectivePrice = (product: Product | null): number | null => {
  if (!product) return null;
  const discount = Number(product.Discount || 0);
  const costPrice = Number(product.CostPrice || 0);
  if (discount > 0 && discount < costPrice) {
    return discount;
  }
  if (Number.isFinite(costPrice) && costPrice > 0) {
    return costPrice;
  }
  return null;
};

export function ProfessionalServicesLandingClient() {
  const [monthlyProduct, setMonthlyProduct] = useState<Product | null>(null);
  const [annualProduct, setAnnualProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cycle, setCycle] = useState<BillingCycle>("annual");

  useEffect(() => {
    let active = true;

    const load = async () => {
      const allProducts = await getProductsClient();
      if (!active) return;
      const monthlyCode = PROFESSIONAL_SERVICES_MONTHLY_PRODUCT_CODE.toLowerCase();
      const annualCode = PROFESSIONAL_SERVICES_ANNUAL_PRODUCT_CODE.toLowerCase();
      const monthly = allProducts.find((p) => (p.ProductCode || "").trim().toLowerCase() === monthlyCode) ?? null;
      const annual = allProducts.find((p) => (p.ProductCode || "").trim().toLowerCase() === annualCode) ?? null;
      setMonthlyProduct(monthly);
      setAnnualProduct(annual);
      setIsLoading(false);
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  const selectedProduct = cycle === "annual" ? annualProduct : monthlyProduct;
  const selectedCode = cycle === "annual" ? PROFESSIONAL_SERVICES_ANNUAL_PRODUCT_CODE : PROFESSIONAL_SERVICES_MONTHLY_PRODUCT_CODE;

  const monthlyPrice = useMemo(() => getEffectivePrice(monthlyProduct), [monthlyProduct]);
  const annualPrice = useMemo(() => getEffectivePrice(annualProduct), [annualProduct]);
  const annualSaving = useMemo(() => {
    if (!monthlyPrice || !annualPrice) return null;
    const saving = monthlyPrice * 12 - annualPrice;
    return saving > 0 ? saving : null;
  }, [monthlyPrice, annualPrice]);

  const legacyPriceLabel = useMemo(() => {
    const fmt = (n: number) => new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);
    return cycle === "annual" ? fmt(PROFESSIONAL_SERVICES_ANNUAL_PRICE_LEGACY) : fmt(PROFESSIONAL_SERVICES_MONTHLY_PRICE_LEGACY);
  }, [cycle]);

  const selectedPriceLabel = useMemo(() => {
    const value = cycle === "annual" ? annualPrice : monthlyPrice;
    if (!value) {
      return "Price available at checkout";
    }
    if (cycle === "annual") {
      return formatZAR(value);
    }
    return `${formatZAR(value)}/mo`;
  }, [annualPrice, cycle, monthlyPrice]);

  return (
    <div className="min-h-screen bg-white px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto flex max-w-6xl items-center justify-center">
        <section className="w-full max-w-[460px] overflow-hidden rounded-[2rem] border border-[#eadfce] bg-white shadow-[0_30px_60px_-32px_rgba(69,38,14,0.38)]">
          <div className="px-6 pb-4 pt-8 text-center sm:px-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9a7a45]">
              Business Owner Lane
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-[#001489] sm:text-4xl">
              Professional Services
            </h1>
                        <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-[#0077B6]">
              One premium profile for consultants, firms, specialists, and service professionals.
            </p>
            <p className="mt-3 inline-block rounded-full bg-emerald-100 border border-emerald-300 px-4 py-1 text-xs font-bold text-emerald-800">
              🎉 Early Adopters Promo — R500/month or R5,000/year • Lock in for life
            </p>
          </div>

          <div className="px-6 pb-8 sm:px-8">
            <div className="mb-5 flex items-center justify-center rounded-2xl bg-[#f5f0e8] p-1">
              <button
                type="button"
                onClick={() => setCycle("annual")}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
                  cycle === "annual"
                    ? "bg-white text-[#001489] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Annual
                {annualSaving ? (
                  <span className="ml-1.5 rounded-full bg-[#ffb81c] px-2 py-0.5 text-[10px] font-bold text-white">
                    Save {formatZAR(annualSaving)}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => setCycle("monthly")}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
                  cycle === "monthly"
                    ? "bg-white text-[#001489] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Monthly
              </button>
            </div>

            <div className="overflow-hidden rounded-[1.6rem] border border-[#f0e2d0] bg-[#fcf8f2] shadow-[0_16px_34px_-26px_rgba(34,22,10,0.45)]">
              <div className="relative aspect-[16/9] w-full overflow-hidden">
                {selectedProduct?.ImageUrl ? (
                  <Image
                    src={selectedProduct.ImageUrl}
                    alt="Professional Services"
                    fill
                    className="object-cover"
                    sizes="460px"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#001489] to-[#0077B6] p-4 text-center text-sm font-semibold text-white">
                    Professional Services
                  </div>
                )}
              </div>

              <div className="space-y-4 px-6 py-6">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-[#001489]">
                    Professional Services Discovery
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Turn your professional profile into a trusted discovery asset on Google and AI.
                  </p>

                  {/* Price block */}
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#9a7a45]">
                      {cycle === "annual" ? "Annual subscription" : "Monthly subscription"}
                    </p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-2">
                      <span className="text-lg line-through text-slate-400">{legacyPriceLabel}</span>
                      <span className="text-4xl font-extrabold text-slate-900">{selectedPriceLabel}</span>
                      <span className="rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[10px] font-bold text-emerald-800 self-center">Early Adopters Promo</span>
                    </div>
                    <p className="mt-0.5 text-xs font-semibold text-emerald-700">Lock in for life while active</p>

                    {cycle === "annual" && annualPrice ? (
                      <>
                        <p className="mt-0.5 text-sm text-slate-500">
                          per year &middot; auto-renews annually
                        </p>
                        {annualSaving ? (
                          <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
                            <span className="text-emerald-600">✓</span>
                            You save {formatZAR(annualSaving)} every year vs monthly billing
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {cycle === "monthly" && monthlyPrice ? (
                      <>
                        <p className="mt-0.5 text-sm text-slate-500">
                          per month &middot; auto-renews monthly
                        </p>
                        {annualSaving ? (
                          <p className="mt-2 text-sm text-slate-500">
                            Switch to annual and save {formatZAR(annualSaving)}/yr
                          </p>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>

                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-[#ffb81c]">✓</span>
                    Professional Services category lane with focused discovery
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-[#ffb81c]">✓</span>
                    {cycle === "annual"
                      ? "Single annual payment — no monthly admin, auto-renews each year"
                      : "Flexible monthly billing via PayFast — cancel anytime"}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-[#ffb81c]">✓</span>
                    Strong trust, contact, and local relevance profile fields
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-[#ffb81c]">✓</span>
                    AI and search-friendly profile structure for better visibility
                  </li>
                </ul>

                <Link
                  href={`/create-profile/professional-services/apply?plan=${selectedCode}`}
                  className="mt-2 block rounded-2xl bg-[#ffb81c] px-5 py-4 text-center text-lg font-semibold text-white shadow-[0_18px_30px_-18px_rgba(255,184,28,0.9)] transition hover:bg-[#f0aa12]"
                >
                  {cycle === "annual" ? "Get Annual Plan — Sign Up" : "Get Monthly Plan — Sign Up"}
                </Link>

                {isLoading ? (
                  <p className="text-center text-xs text-slate-400">Loading plan details...</p>
                ) : null}
                {!isLoading && !selectedProduct ? (
                  <p className="text-center text-xs text-amber-700">
                    Plan details unavailable — confirm the product is active in admin.
                  </p>
                ) : null}

                <p className="text-center text-xs text-slate-400">
                  Secure checkout via PayFast &middot; ZAR pricing &middot; cancel anytime
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}